import { Upload } from 'lucide-react';
import { useRef, useState } from 'react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { useImportBankStatement } from '@/hooks/react-query/useBankIncoming';
import type {
  RowRejection,
  StatementImportResult,
} from '@/services/bankIncomingService';
import { APIError } from '@/utils/api-client';

export interface StatementImportPanelProps {
  fundId: string;
}

interface ImportErrorDetail {
  code?: string;
  rejections?: RowRejection[];
  /** Names what the code cannot, e.g. which accounts the fund doesn't hold. */
  context?: string | null;
}

const DEFAULT_ERROR_MESSAGE =
  "The statement could not be imported. Check that the file is a Rabobank or Ebury CSV export drawn on this fund's accounts.";

// Unrecognised codes fall back to the generic message, so a future backend
// code never renders blank.
const ERROR_MESSAGES: Record<string, string> = {
  account_not_found:
    "This file covers an account this fund doesn't hold. Check you uploaded it against the right fund, and that every account it covers has been added.",
  client_reference_missing:
    "This Ebury export names no client reference, so there is nothing to look the fund's accounts up by. Re-export it without editing it first.",
  unexpected_header:
    "This file's columns don't match the export layout this importer expects.",
  unrecognised_format:
    'This file is neither a Rabobank nor an Ebury CSV export. Only those two can be uploaded here.',
  no_rows: 'Every row in this file was rejected - nothing could be staged.',
  not_a_csv: 'Only a Rabobank or Ebury CSV export can be uploaded here.',
  malformed_csv:
    'This file could not be read as CSV. Re-export it from the bank without editing it first.',
  file_too_large:
    'This file is too large to import. Export a shorter date range and upload it in parts.',
};

/** Reads the backend's {code, rejections} detail off a failed import. */
function describeImportError(error: unknown): {
  message: string;
  context: string | null;
  rejections: RowRejection[];
} {
  if (
    error instanceof APIError &&
    error.data &&
    typeof error.data === 'object'
  ) {
    const detail = (error.data as { detail?: unknown }).detail;
    if (detail && typeof detail === 'object') {
      const { code, rejections, context } = detail as ImportErrorDetail;
      return {
        message: (code && ERROR_MESSAGES[code]) || DEFAULT_ERROR_MESSAGE,
        context: context ?? null,
        rejections: rejections ?? [],
      };
    }
  }
  return { message: DEFAULT_ERROR_MESSAGE, context: null, rejections: [] };
}

/** Why the upload failed, and any rows named on the way down. */
function ImportErrorAlert({ error }: { error: unknown }) {
  const { message, context, rejections } = describeImportError(error);

  return (
    <Alert variant="destructive">
      <AlertDescription>
        {message}
        {context && <span className="mt-1 block">Missing: {context}</span>}
        {rejections.length > 0 && (
          <ul className="list-disc pl-5 mt-1">
            {rejections.map((rejection) => (
              <li key={rejection.line_number}>
                Line {rejection.line_number}: {rejection.reason}
              </li>
            ))}
          </ul>
        )}
      </AlertDescription>
    </Alert>
  );
}

/**
 * Uploads a bank statement export. Nothing is booked: the engine runs
 * suggest-only, so every proposal lands in the review queue.
 */
export function StatementImportPanel({ fundId }: StatementImportPanelProps) {
  const [file, setFile] = useState<File | null>(null);
  const [result, setResult] = useState<StatementImportResult | null>(null);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const importStatement = useImportBankStatement({ fundId });

  // A dropped file never reaches the input, so both paths go through here.
  const choose = (chosen: File | null) => {
    setFile(chosen);
    setResult(null);
    if (inputRef.current && chosen === null) inputRef.current.value = '';
  };

  const run = (dryRun: boolean) => {
    if (!file) return;
    // Or a failed re-run leaves a stale summary under the error alert.
    setResult(null);
    importStatement.mutate(
      { file, options: { dryRun, autoSettleInternalTransfers: false } },
      { onSuccess: setResult }
    );
  };

  return (
    <Card className="space-y-4 p-4">
      <div>
        <h2 className="text-lg font-semibold">Import Bank Transactions</h2>
        <p className="text-sm text-muted-foreground">
          Staged lines land in the incoming transactions queue under General
          Ledger.
        </p>
      </div>

      <div className="space-y-1">
        {/* The input stays in the tab order rather than hidden, so the zone is
            reachable by keyboard; the label makes the whole area the target. */}
        <input
          id="statement-file"
          ref={inputRef}
          type="file"
          accept=".csv,text/csv"
          className="peer sr-only"
          onChange={(event) => choose(event.target.files?.[0] ?? null)}
        />
        <label
          htmlFor="statement-file"
          onDragOver={(event) => {
            event.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(event) => {
            event.preventDefault();
            setDragging(false);
            choose(event.dataTransfer.files?.[0] ?? null);
          }}
          className={`flex cursor-pointer flex-col items-center gap-2 rounded-lg border-2 border-dashed px-4 py-8 text-center transition-colors peer-focus-visible:ring-2 peer-focus-visible:ring-ring peer-focus-visible:ring-offset-2 ${
            dragging
              ? 'border-primary bg-primary/5'
              : 'border-muted-foreground/25 hover:border-muted-foreground/50 hover:bg-muted/50'
          }`}
        >
          <Upload className="h-8 w-8 text-muted-foreground" />
          {file ? (
            <>
              <span className="text-sm font-medium">{file.name}</span>
              <span className="text-xs text-muted-foreground">
                Choose a different file, or drop one here
              </span>
            </>
          ) : (
            <>
              <span className="text-sm font-medium">
                Choose a Rabobank or Ebury CSV export
              </span>
              <span className="text-xs text-muted-foreground">
                or drop one here
              </span>
            </>
          )}
        </label>
        <p className="text-xs text-muted-foreground">
          The engine only suggests matches - nothing is posted to the ledger.
          Re-uploading an overlapping export adds nothing new.
        </p>
      </div>

      <div className="flex gap-2">
        <Button
          variant="outline"
          disabled={!file || importStatement.isPending}
          onClick={() => run(true)}
        >
          Preview
        </Button>
        <Button
          disabled={!file || importStatement.isPending}
          onClick={() => run(false)}
        >
          Import
        </Button>
      </div>

      {importStatement.isError && (
        <ImportErrorAlert error={importStatement.error} />
      )}

      {result && (
        <div className="space-y-2 text-sm">
          <p>
            {result.rows_read} rows read &middot; {result.inserted} staged
            &middot; {result.duplicates} already present
            {result.already_received > 0 &&
              ` · ${result.already_received} already received from Ponto`}
            {result.dry_run
              ? ' (preview only, nothing written)'
              : ` · ${result.lines_suggested} suggested (account total after this run, not just this file)`}
          </p>
          {result.rejections.length > 0 && (
            <div>
              <p className="font-medium">Rows not imported</p>
              <ul className="list-disc pl-5">
                {result.rejections.map((rejection) => (
                  <li key={rejection.line_number}>
                    Line {rejection.line_number}: {rejection.reason}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}
